import { useCallback, useEffect, useState } from 'react';

import { ErroreApi } from '../api/client.ts';
import { categorie as apiCategorie, tipiScadenza as apiTipi, type DatiTipo } from '../api/scadenziario.ts';
import type { Categoria, TipoScadenza } from '../api/tipi-dati.ts';
import { Dialogo } from '../componenti/Dialogo.tsx';
import { SelettoreDestinatari } from '../componenti/SelettoreDestinatari.tsx';
import {
  DialogoEliminazione,
  ZonaPericolosa,
  type BersaglioEliminazione,
} from '../componenti/DialogoEliminazione.tsx';
import { descriviPeriodicita } from '../componenti/rinnovo.ts';
import { descriviStadi, soglieStadi } from '../componenti/stadi.ts';
import { useAutenticazione } from '../auth/contesto.tsx';
import { IntestazioneOrdinabile, useOrdinamento } from '../componenti/IntestazioneOrdinabile.tsx';
import { ordinaRighe, type Valore } from '../componenti/ordinamento.ts';
import { puoRedigere } from '../auth/ruoli.ts';

/*
 * Le categorie arrivano dall'API e non da un elenco scritto qui: dal punto 3b
 * le crea l'ufficio. Si leggono una volta e si passano ai due punti che ne
 * hanno bisogno — il filtro in cima e il menu del form — invece di chiederle
 * due volte.
 */
function useCategorie(attiveSoltanto: boolean) {
  const [categorie, setCategorie] = useState<Categoria[]>([]);

  useEffect(() => {
    let annullato = false;

    apiCategorie
      .elenca(attiveSoltanto ? { attiva: 'true', limite: 200 } : { limite: 200 })
      .then((r) => { if (!annullato) setCategorie(r.elementi); })
      .catch(() => undefined);

    return () => { annullato = true; };
  }, [attiveSoltanto]);

  return categorie;
}

/**
 * Catalogo dei tipi di scadenza — i modelli riusabili.
 *
 * Un tipo non è una scadenza: è lo stampo ("Visita medica, ogni 12 mesi,
 * avvisa 30 giorni prima"). È quello che permette il rinnovo automatico, e
 * quello che evita di ritrovarsi "Visita medica", "visita medica" e
 * "Vis. medica" come tre cose distinte.
 *
 * Solo gli amministratori possono modificarlo: un tipo sbagliato si propaga a
 * tutte le scadenze che lo useranno.
 */
/**
 * ⚠️ La periodicità si ordina sui **mesi**, non sulla frase «ogni 12 mesi»:
 * come testo, «ogni 6 mesi» verrebbe dopo «ogni 24 mesi». I tipi una tantum
 * non hanno mesi e finiscono in fondo, che è dove ci si aspetta di trovarli.
 */
function valoreTipo(t: TipoScadenza, chiave: string): Valore {
  switch (chiave) {
    case 'categoria': return t.categoria.nome;
    case 'nome': return t.nome;
    case 'periodicita': return t.periodicitaMesi;
    case 'preavviso': return t.preavvisoGiorni;
    case 'aperte': return t.scadenzeAperte ?? 0;
    default: return null;
  }
}

export function Tipi() {
  const { ordinamento, ordina } = useOrdinamento();
  const { utente } = useAutenticazione();
  const puoModificare = puoRedigere(utente?.ruolo);

  const [elementi, setElementi] = useState<TipoScadenza[]>([]);
  const [categoria, setCategoria] = useState('');
  const [mostraDisattivati, setMostraDisattivati] = useState(false);
  const [inCaricamento, setInCaricamento] = useState(true);
  const [errore, setErrore] = useState<string | null>(null);
  const [conferma, setConferma] = useState<string | null>(null);
  const [inForm, setInForm] = useState<TipoScadenza | null | undefined>(undefined);
  const categorieFiltro = useCategorie(false);
  /** Il tipo per cui è aperta la conferma di cancellazione definitiva. */
  const [daEliminare, setDaEliminare] = useState<BersaglioEliminazione | null>(null);
  const [ricarica, setRicarica] = useState(0);

  useEffect(() => {
    let annullato = false;
    setInCaricamento(true);

    apiTipi
      .elenca({
        ...(categoria !== '' ? { categoria } : {}),
        ...(mostraDisattivati ? {} : { attivo: 'true' }),
        limite: 500,
      })
      .then((r) => { if (!annullato) { setElementi(r.elementi); setErrore(null); } })
      .catch((e: unknown) => {
        if (!annullato) setErrore(e instanceof Error ? e.message : 'Errore imprevisto');
      })
      .finally(() => { if (!annullato) setInCaricamento(false); });

    return () => { annullato = true; };
  }, [categoria, mostraDisattivati, ricarica]);

  const dopoAzione = useCallback((messaggio: string) => {
    setInForm(undefined);
    setDaEliminare(null);
    setConferma(messaggio);
    setRicarica((n) => n + 1);
  }, []);

  async function cambiaStato(t: TipoScadenza) {
    try {
      if (t.attivo) await apiTipi.disattiva(t.id);
      else await apiTipi.riattiva(t.id);
      dopoAzione(`«${t.nome}» ${t.attivo ? 'disattivato' : 'riattivato'}.`);
    } catch (e) {
      setErrore(e instanceof ErroreApi ? e.message : 'Errore imprevisto');
    }
  }

  return (
    <>
      <div className="titolo-pagina">
        <h1>Tipi di scadenza</h1>
        <span className="conteggio">{elementi.length} nel catalogo</span>
        {puoModificare && (
          <button
            type="button"
            className="bottone bottone-principale azione-titolo"
            onClick={() => setInForm(null)}
          >
            <span aria-hidden="true">+</span> Nuovo tipo
          </button>
        )}
      </div>

      <p className="sottotitolo-pagina">
        Un tipo è il modello riusabile: definisce ogni quanto una scadenza si ripete e con
        quanto anticipo avvisare. È quello che permette il rinnovo automatico quando una
        scadenza viene completata.
        {!puoModificare && ' Solo un amministratore o un redattore può modificarlo.'}
      </p>

      {conferma !== null && (
        <div className="avviso-esito" role="status">
          {conferma}
          <button type="button" className="bottone-piatto" onClick={() => setConferma(null)}>Chiudi</button>
        </div>
      )}
      {errore !== null && <div className="avviso-errore" role="alert">{errore}</div>}

      <div className="filtri">
        <div className="campo">
          <label htmlFor="t-categoria">Categoria</label>
          <select id="t-categoria" value={categoria} onChange={(e) => setCategoria(e.target.value)}>
            <option value="">Tutte le categorie</option>
            {categorieFiltro.map((c) => (
              <option key={c.id} value={c.id}>{c.nome}</option>
            ))}
          </select>
        </div>

        <label className="interruttore">
          <input
            type="checkbox"
            checked={mostraDisattivati}
            onChange={(e) => setMostraDisattivati(e.target.checked)}
          />
          Mostra anche i disattivati
        </label>
      </div>

      <div className="pannello">
        {inCaricamento ? (
          <p className="stato-caricamento">Caricamento…</p>
        ) : elementi.length === 0 ? (
          <p className="stato-vuoto">
            Il catalogo è vuoto. {puoModificare ? 'Creane uno per iniziare.' : ''}
          </p>
        ) : (
          <div className="tabella-contenitore">
            <table className="tabella-centrata">
              <caption className="solo-lettori-schermo">
                Catalogo dei tipi di scadenza, con periodicità e preavviso
              </caption>
              <thead>
                <tr>
                  <IntestazioneOrdinabile chiave="categoria" ordinamento={ordinamento} onOrdina={ordina}>Categoria</IntestazioneOrdinabile>
                  <IntestazioneOrdinabile chiave="nome" ordinamento={ordinamento} onOrdina={ordina}>Nome</IntestazioneOrdinabile>
                  <IntestazioneOrdinabile chiave="periodicita" ordinamento={ordinamento} onOrdina={ordina}>Periodicità</IntestazioneOrdinabile>
                  <IntestazioneOrdinabile chiave="preavviso" ordinamento={ordinamento} onOrdina={ordina}>Preavviso</IntestazioneOrdinabile>
                  <IntestazioneOrdinabile chiave="aperte" ordinamento={ordinamento} onOrdina={ordina}>Scadenze aperte</IntestazioneOrdinabile>
                  {puoModificare && <th scope="col" className="col-azione">Azione</th>}
                </tr>
              </thead>
              <tbody>
                {ordinaRighe(elementi, ordinamento, valoreTipo).map((t) => (
                  <tr key={t.id} className={t.attivo ? '' : 'riga-annullata'}>
                    <td className="tipo-nome">
                      {t.categoria.nome}
                      {/* Un tipo che ha perso la categoria non è un errore da
                          nascondere: è una riga da risistemare, e se non si
                          vede nessuno la risistema. */}
                      {t.categoria.id === null && (
                        <span className="tenue"> · da riassegnare</span>
                      )}
                    </td>
                    <td>
                      <span className="soggetto-nome">{t.nome}</span>
                      {!t.attivo && <span className="badge badge-annullata" style={{ marginLeft: 8 }}>
                        <span aria-hidden="true">⊘</span> Disattivato
                      </span>}
                    </td>
                    <td className="tipo-nome">{descriviPeriodicita(t.periodicitaMesi)}</td>
                    <td className="numero">{t.preavvisoGiorni} gg</td>
                    <td className="numero">{t.scadenzeAperte ?? 0}</td>
                    {puoModificare && (
                      <td className="col-azione">
                        <div className="cella-azioni">
                          <button type="button" className="bottone" onClick={() => setInForm(t)}>
                            <span aria-hidden="true">✎</span> Modifica
                          </button>
                          <button type="button" className="bottone" onClick={() => void cambiaStato(t)}>
                            {t.attivo ? 'Disattiva' : 'Riattiva'}
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {inForm !== undefined && (
        <DialogoTipo
          tipo={inForm}
          // Il catalogo lo tocca solo un amministratore, quindi qui il permesso
          // di modificare e quello di eliminare coincidono.
          puoEliminare={puoModificare}
          onChiudi={() => setInForm(undefined)}
          onSalvato={dopoAzione}
          onElimina={(t) => {
            setInForm(undefined);
            setDaEliminare({
              entita: 'tipo',
              id: t.id,
              nome: t.nome,
              etichettaNome: 'il nome del tipo',
            });
          }}
        />
      )}

      {daEliminare !== null && (
        <DialogoEliminazione
          bersaglio={daEliminare}
          onChiudi={() => setDaEliminare(null)}
          onEliminato={dopoAzione}
        />
      )}
    </>
  );
}

function DialogoTipo({
  tipo, puoEliminare, onChiudi, onSalvato, onElimina,
}: {
  tipo: TipoScadenza | null;
  puoEliminare: boolean;
  onChiudi: () => void;
  onSalvato: (messaggio: string) => void;
  onElimina: (tipo: TipoScadenza) => void;
}) {
  /*
   * La categoria scelta nel form.
   *
   * `0` significa «non ancora scelta»: un tipo che ha perso la sua categoria
   * (0009) apre il form senza nessuna preselezionata, ed è la verità — non
   * c'è nessuna voce del catalogo da rimettere lì al posto suo. Il salvataggio
   * la richiede, così riaprire quel tipo obbliga a risistemarlo.
   */
  const [categoria, setCategoria] = useState<number>(tipo?.categoria.id ?? 0);
  const categorieForm = useCategorie(true);
  const [nome, setNome] = useState(tipo?.nome ?? '');
  const [ricorrente, setRicorrente] = useState(tipo === null || tipo.periodicitaMesi !== null);
  const [periodicita, setPeriodicita] = useState(tipo?.periodicitaMesi ?? 12);
  const [preavviso, setPreavviso] = useState(tipo?.preavvisoGiorni ?? 30);
  const [note, setNote] = useState(tipo?.note ?? '');
  const [destinatari, setDestinatari] = useState<number[]>([]);
  const [errore, setErrore] = useState<string | null>(null);
  const [inCorso, setInCorso] = useState(false);

  /*
   * In modifica i destinatari si leggono dalla scheda del tipo.
   *
   * L'elenco non li porta con sé — sarebbe una join per una colonna che la
   * tabella non mostra — quindi l'oggetto arrivato da lì non li ha. Senza
   * questa lettura il form partirebbe con le caselle vuote e **salvarlo
   * senza toccarle cancellerebbe i destinatari di quel tipo**, in silenzio.
   */
  useEffect(() => {
    if (tipo === null) return;
    let annullato = false;

    void apiTipi
      .ottieni(tipo.id)
      .then((t) => { if (!annullato) setDestinatari((t.destinatari ?? []).map((d) => d.id)); })
      .catch(() => undefined);

    return () => { annullato = true; };
  }, [tipo]);

  async function salva() {
    setErrore(null);
    setInCorso(true);

    const dati: DatiTipo = {
      categoriaId: categoria,
      nome: nome.trim(),
      periodicitaMesi: ricorrente ? periodicita : null,
      preavvisoGiorni: preavviso,
      note: note.trim() === '' ? null : note.trim(),
      destinatari,
    };

    try {
      if (tipo === null) {
        const creato = await apiTipi.crea(dati);
        onSalvato(`Creato il tipo «${creato.nome}».`);
      } else {
        await apiTipi.modifica(tipo.id, dati);
        onSalvato(`«${dati.nome}» aggiornato.`);
      }
    } catch (e) {
      setErrore(e instanceof ErroreApi ? e.message : 'Errore imprevisto');
      setInCorso(false);
    }
  }

  return (
    <Dialogo titolo={tipo === null ? 'Nuovo tipo di scadenza' : 'Modifica tipo'} aperto onChiudi={onChiudi}>
      {errore !== null && <div className="avviso-errore" role="alert">{errore}</div>}

      <form onSubmit={(e) => { e.preventDefault(); void salva(); }}>
        <div className="campo">
          <label htmlFor="t-cat">Categoria <span className="obbligatorio">obbligatoria</span></label>
          <select id="t-cat" value={categoria} onChange={(e) => setCategoria(Number(e.target.value))}>
            {/* La voce vuota compare solo quando non c'è niente di scelto:
                per un tipo che ha perso la categoria è l'unica opzione onesta,
                e obbliga a sceglierne una per poter salvare. */}
            {categoria === 0 && <option value={0} disabled>Scegli…</option>}
            {categorieForm.map((c) => (
              <option key={c.id} value={c.id}>{c.nome}</option>
            ))}
          </select>
        </div>

        <div className="campo">
          <label htmlFor="t-nome">Nome <span className="obbligatorio">obbligatorio</span></label>
          <input
            id="t-nome"
            type="text"
            value={nome}
            onChange={(e) => setNome(e.target.value)}
            placeholder="es. Visita medica, Taratura strumenti, Versamento F24"
            required
            autoFocus
          />
          <p className="aiuto">Dev'essere unico nella sua categoria.</p>
        </div>

        <fieldset className="blocco-rinnovo">
          <legend><span aria-hidden="true">⟳</span> Ripetizione</legend>

          <label className="interruttore">
            <input
              type="checkbox"
              checked={ricorrente}
              onChange={(e) => setRicorrente(e.target.checked)}
            />
            Questa scadenza si ripete
          </label>

          {ricorrente ? (
            <div className="campo" style={{ marginTop: 12, marginBottom: 0 }}>
              <label htmlFor="t-per">Ogni quanti mesi</label>
              <div className="campo-affiancato">
                <input
                  id="t-per"
                  type="number"
                  min={1}
                  max={600}
                  value={periodicita}
                  onChange={(e) => setPeriodicita(Number(e.target.value))}
                  style={{ minWidth: '90px' }}
                />
                <span className="aiuto">{descriviPeriodicita(periodicita)}</span>
              </div>
              <p className="aiuto">
                Completando una scadenza di questo tipo, la successiva verrà creata da sola
                a questa distanza dalla data in cui è stata fatta.
              </p>
            </div>
          ) : (
            <p className="aiuto" style={{ marginTop: 10 }}>
              Una tantum: completandola non verrà generata nessuna scadenza successiva.
            </p>
          )}
        </fieldset>

        <div className="campo">
          <label htmlFor="t-pre">Giorni di preavviso</label>
          <div className="campo-affiancato">
            <input
              id="t-pre"
              type="number"
              min={0}
              max={3650}
              value={preavviso}
              onChange={(e) => setPreavviso(Number(e.target.value))}
              style={{ minWidth: '90px' }}
            />
            <span className="aiuto">giorni prima della data</span>
          </div>
          <p className="aiuto">
            Da quanti giorni prima la scadenza risulta «in scadenza». Vale per questo tipo
            soltanto: una revisione può richiedere più margine di una visita medica.
          </p>
          <AnteprimaAvvisi giorni={preavviso} />
        </div>

        <div className="campo">
          <label htmlFor="t-note">Note <span className="tenue">facoltative</span></label>
          <textarea id="t-note" rows={3} value={note} onChange={(e) => setNote(e.target.value)} />
        </div>

        <SelettoreDestinatari
          selezionati={destinatari}
          onCambia={setDestinatari}
          descrizione="Chi verrà avvisato, di norma, per le scadenze di questo tipo. È un punto di partenza: ogni scadenza copia questa lista alla creazione e poi può essere ritoccata da sola. Cambiare qui non tocca le scadenze già inserite."
        />


        <div className="dialogo-azioni">
          <button type="submit" className="bottone bottone-principale" disabled={inCorso}>
            {inCorso ? 'Salvataggio…' : tipo === null ? 'Crea tipo' : 'Salva modifiche'}
          </button>
          <button type="button" className="bottone-piatto" onClick={onChiudi} disabled={inCorso}>
            Annulla
          </button>
        </div>

        {tipo !== null && (
          <p className="aiuto tracciabilita">
            Modificare periodicità o preavviso vale per i rinnovi futuri: le scadenze già
            create conservano i valori con cui sono nate.
          </p>
        )}
      </form>

      {tipo !== null && puoEliminare && (
        <ZonaPericolosa
          spiegazione={
            'Toglie il tipo dal catalogo per sempre. Se ci sono scadenze di questo tipo verrà ' +
            'chiesto che fine devono fare: sparire con lui, oppure restare nello storico ' +
            'orfane, col nome del tipo congelato sulla riga. ' +
            "Per smettere di proporlo nei menu lasciando in piedi i collegamenti c'è «Disattiva»."
          }
          onElimina={() => onElimina(tipo)}
        />
      )}
    </Dialogo>
  );
}

/**
 * «Gli avvisi partiranno a 30, 15 e 7 giorni» — sotto al campo, mentre si scrive.
 *
 * Serve a far capire uno schema che altrimenti si scoprirebbe solo ricevendo
 * le email: da un numero solo ne nascono tre avvisi, e non sono equidistanti.
 * Chi scrive 180 deve vedere subito che il secondo arriva a 90 e l'ultimo a 7,
 * non a 45.
 *
 * ⚠️ Il calcolo è duplicato dal backend (`componenti/stadi.ts` spiega perché).
 * **Se cambia lì, va cambiato anche qui**, o questa riga diventa una bugia
 * detta a qualcuno che si sta fidando.
 */
function AnteprimaAvvisi({ giorni }: { giorni: number }) {
  // Il campo è un `number` legato a un input: cancellarlo del tutto produce
  // NaN, e senza questo controllo comparirebbe «a NaN giorni» mentre si
  // riscrive il valore.
  if (!Number.isFinite(giorni) || giorni < 0) return null;

  const soglie = soglieStadi(giorni);

  return (
    <p className="aiuto anteprima-avvisi">
      <span aria-hidden="true">◔</span>{' '}
      {soglie.length === 1 ? 'Partirà un avviso ' : `Partiranno ${soglie.length} avvisi `}
      <strong>{descriviStadi(giorni)}</strong> dalla scadenza
      {soglie.length < 3 && giorni > 0 && (
        <>
          {' '}
          <span className="tenue">
            — sono meno di tre perché con un preavviso così corto cadrebbero troppo
            vicini fra loro
          </span>
        </>
      )}
      .
    </p>
  );
}
